import { useState } from "react";
import { ImageIcon, Grid3x3 } from "lucide-react";
import { assetUrl } from "../lib/api";
import PhotoLightbox from "./PhotoLightbox";

/**
 * Airbnb-style yacht gallery: 1 large + 4 small on desktop, swipeable
 * carousel on mobile. Every tile opens the full-screen PhotoLightbox at
 * that photo's index — user can then arrow / swipe / keyboard through
 * ALL uploaded photos (not just the 5 grid tiles).
 */
export default function YachtGallery({ images, yachtName }) {
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const imgs = images && images.length ? images : [];

  function open(i) {
    setLightboxIdx(i);
  }
  function close() {
    setLightboxIdx(-1);
  }

  if (imgs.length === 0) {
    return (
      <div
        className="w-full h-[380px] md:h-[520px] bg-hbe-soft rounded-xl flex flex-col items-center justify-center gap-3 text-hbe-accent"
        data-testid="yacht-gallery-empty"
      >
        <ImageIcon size={44} strokeWidth={1.3} />
        <div className="text-sm">Photos coming soon</div>
      </div>
    );
  }

  const [main, ...thumbs] = imgs;
  const displayThumbs = thumbs.slice(0, 4);

  return (
    <div data-testid="yacht-gallery">
      {/* Desktop grid — 1 large + 4 small */}
      <div className="hidden md:block relative">
        <div className="grid grid-cols-4 grid-rows-2 gap-2 h-[520px] rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => open(0)}
            className="col-span-2 row-span-2 relative overflow-hidden group cursor-zoom-in"
            data-testid="gallery-main"
            aria-label={`${yachtName} — open full-size photo 1`}
          >
            <img
              src={assetUrl(main)}
              alt={`${yachtName} — feature photograph`}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
              loading="eager"
            />
          </button>
          {displayThumbs.map((src, i) => (
            <button
              type="button"
              key={src}
              onClick={() => open(i + 1)}
              className="relative overflow-hidden group cursor-zoom-in"
              data-testid={`gallery-thumb-${i}`}
              aria-label={`${yachtName} — open full-size photo ${i + 2}`}
            >
              <img
                src={assetUrl(src)}
                alt={`${yachtName} — detail photograph ${i + 1}`}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                loading="lazy"
              />
            </button>
          ))}
        </div>
        {/* Airbnb-style "Show all photos" pill */}
        <button
          type="button"
          onClick={() => open(0)}
          className="absolute bottom-4 right-4 inline-flex items-center gap-2 bg-white text-hbe-primary text-sm font-medium px-4 py-2 rounded-lg border border-hbe-primary/20 shadow-sm hover:shadow-md hover:bg-white transition"
          data-testid="gallery-show-all"
        >
          <Grid3x3 size={16} />
          Show all photos
        </button>
      </div>

      {/* Mobile: horizontal snap carousel */}
      <div className="md:hidden relative">
        <div
          className="-mx-6 px-6 flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2"
          data-testid="gallery-mobile"
        >
          {imgs.map((src, i) => (
            <button
              type="button"
              key={src}
              onClick={() => open(i)}
              className="snap-center shrink-0 w-[85%] rounded-lg overflow-hidden aspect-[4/3] cursor-zoom-in"
              data-testid={`gallery-mobile-${i}`}
              aria-label={`${yachtName} — open full-size photo ${i + 1}`}
            >
              <img
                src={assetUrl(src)}
                alt={`${yachtName} — photograph ${i + 1}`}
                className="w-full h-full object-cover"
                loading={i === 0 ? "eager" : "lazy"}
              />
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => open(0)}
          className="absolute bottom-4 right-4 inline-flex items-center gap-2 bg-white text-hbe-primary text-sm font-medium px-3 py-1.5 rounded-lg border border-hbe-primary/20 shadow-sm"
          data-testid="gallery-show-all-mobile"
        >
          <Grid3x3 size={14} />
          Show all photos
        </button>
      </div>

      <PhotoLightbox
        photos={imgs}
        initialIndex={Math.max(0, lightboxIdx)}
        isOpen={lightboxIdx >= 0}
        onClose={close}
        altPrefix={`${yachtName} photograph`}
      />
    </div>
  );
}
